// Estado, regras e handlers do cartão de realização MAD (`TaskItemMad`), separados do JSX.
// Corpo movido de `index.tsx` sem edição de lógica (RDN-45): qualquer mudança de regra aqui muda o cartão
// antigo e o novo ao mesmo tempo. Espelho Normal: src/components/task-item.
import { useAuth } from 'context/AuthContext';
import { format, utcToZonedTime } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useModal } from 'context/ModalContext';
import { getPlayerInfo, submitFeedback } from 'services/dashboard.service';
import { canDeleteTask, deleteRequiresReason } from 'components/task-item/delete-rules';
import { CyclesInterface, Step } from 'types/user';
import { Countdown } from 'components/countdown';
import { getTaskStep } from 'utils/helpers';


export interface TaskItemProps {
  data: any;
  playerInfo?: any;
  editable?: boolean;
  canUpdateIndicator?: boolean;
  canEvaluate?: boolean;
  manager?: boolean;
  managerType?: string;
  promote?: boolean;
  double?: boolean;
  reject?: boolean;
  reset?: boolean;
  cycle?: CyclesInterface | Step;
  activeEvaluationStep?: number | null;
  taskStep?: number | undefined;
  playerStep?: number | undefined;
  isView?: boolean | undefined | null;
  canApproveParticipants?: boolean | undefined | null;
  canApproveParticipantsInstExpired?: boolean | undefined | null;
  /**
   * Indica que o gestor que está apreciando tem alçada (nível RO/cod_funcao) para autorizar a
   * quantidade de participantes desta realização. Quando verdadeiro, qualquer apreciação "para
   * frente" aprova a quantidade automaticamente — usado para ajustar o aviso de participantes
   * pendentes. Deve refletir a mesma regra que dispara a auto-aprovação na apreciação.
   */
  isParticipantsApprover?: boolean | undefined | null;
  getAction?: any;
  bonus?: boolean;
  award?: {
    uau: boolean;
    mandou: boolean;
    valeu: boolean;
  };
  onPromote?: (task: any) => void;
  onDouble?: (task: any) => boolean | Promise<boolean> | void | Promise<void>;
  onAccept?: (task: any) => void;
  onReject?: (task: any) => void;
  onClearStatus?: (task: any, status: any) => void;
  onDelete?: (task: any) => void;
  onFeedback?: (task: any) => void;
  onStop?: (task: any) => boolean | Promise<boolean> | void | Promise<void>;
  onReset?: (task: any) => void;
  onReload?: (task: any) => void;
  onAward?: (task: any, badge: 'uau' | 'mandou' | 'valeu') => boolean | Promise<boolean> | void | Promise<void>;
  onUpdateIndicator?: (task: any) => void;
  clearGetAction?: () => void;
  onOpenModalSendMessage?: (params: {
    managerName: string;
    participantCount: number;
    taskTitle: string;
    managerId: string;
  }) => void;
  /**
   * Gate do botão do assessor do pit stop no fluxo MAD (PIT-05), calculado FORA do
   * componente pela regra dupla-aware (isPitStopAssessor de mad/rules). Quando fornecido
   * (boolean) substitui o cálculo interno posicional de `pitStopCanEvaluate`; ausente
   * (undefined) mantém o cálculo original — compatibilidade com o fluxo Normal.
   */
  isPitStopAssessor?: boolean;
  ecoa?: boolean;
}

export interface managersByStepProps {
  blocks: number;
  chave?: string;
  cod_etapa?: number;
  nome_guerra?: string;
  prosseguir_gst?: number;
  stage: string;
  stageTitle: string;
  stageNumber: number;
}

export function useTaskItemMad(props: TaskItemProps) {
  const { showModal, closeModal } = useModal();
  const { user, realCycle } = useAuth();
  const navigate = useNavigate();
  const {
    data,
    editable,
    canUpdateIndicator,
    canEvaluate,
    manager,
    managerType,
    promote,
    double,
    reset,
    playerInfo,
    cycle,
    activeEvaluationStep,
    playerStep,
    isView,
    canApproveParticipants,
    canApproveParticipantsInstExpired,
    isParticipantsApprover,
    getAction,
    bonus,
    award,
    onPromote,
    onDouble,
    onAccept,
    onReject,
    onDelete,
    onFeedback,
    onStop,
    onReset,
    onReload,
    onAward,
    onUpdateIndicator,
    clearGetAction,
    onOpenModalSendMessage,
    isPitStopAssessor,
    ecoa,
  } = props;

  // const [quizAnswered, setQuizAnswered] = useState<boolean>(!!data?.answeredQuiz);
  const taskStep = getTaskStep(data);

  const [oppened, setOppened] = useState<boolean>(false);
  const [status, setStatus] = useState<any>();
  const [showFeedbacks, setShowFeedbacks] = useState(false);
  // const [hasFeedback, setHasFeedback] = useState<boolean | undefined>();
  // const [showModalMatriz, setShowModalMatriz] = useState<boolean>();
  const [realPlayerInfo, setRealPlayerInfo] = useState<any>();
  const [validateTeamMember, setValidateTeamMember] = useState<string>();
  const [userToChange, setUserToChange] = useState<string | undefined>();
  const [realDatetime, setRealDatetime] = useState<number>(0);

  const isVacationManager = useMemo(() => !!playerInfo && !!user && playerInfo._id !== user.extra.id, [playerInfo, user]);

  useEffect(() => {
    // let interv: any = undefined;

    setRealDatetime(data.now);

    // interv = window.setInterval(() => {
    //   setRealDatetime(prevstate => prevstate + 1000);
    // }, 1000);

    // return () => clearInterval(interv);
  }, [data]);

  const isMAD: boolean = useMemo(() => {
    if (!data) return false;
    // return !!data.mad || (Array.isArray(data.non_evaluator) && data.non_evaluator.length > 0);
    return !!data.mad;
  }, [data]);

  const userCodEtapa = useCallback(
    (userI?: any) => {
      if (!userI && !playerInfo) return undefined;

      const user = userI ?? playerInfo;

      const normalize = (val: number) => (typeof val === 'number' ? (val === 1 ? 3 : val > 5 ? 0 : val) : 0);

      const madCodEtapas =
        isMAD && Array.isArray(user.mad_dupla)
          ? user.mad_dupla
              .map((m: any) => (typeof m.cod_etapa === 'number' ? normalize(m.cod_etapa) : null))
              .filter((v: any) => v !== null)
          : [];

      const maxMadCodEtapa = madCodEtapas.length > 0 ? Math.max(...madCodEtapas) : 0;

      const codEtapa =
        maxMadCodEtapa >
        (typeof user.cod_etapa === 'number' ? (user.cod_etapa === 1 ? 3 : user.cod_etapa > 5 ? 0 : user.cod_etapa) : 0)
          ? maxMadCodEtapa
          : typeof user.cod_etapa === 'number'
            ? user.cod_etapa
            : 0;

      const madCodEtapasSub =
        isMAD && Array.isArray(user.mad_dupla)
          ? user.mad_dupla
              .map((m: any) => (typeof m.cod_etapa_sub === 'number' ? normalize(m.cod_etapa_sub) : null))
              .filter((v: any) => v !== null)
          : [];

      const maxMadCodEtapaSub = madCodEtapasSub.length > 0 ? Math.max(...madCodEtapasSub) : 0;

      const codEtapaSub =
        maxMadCodEtapaSub >
        (typeof user.cod_etapa_sub === 'number'
          ? user.cod_etapa_sub === 1
            ? 3
            : user.cod_etapa_sub > 5
              ? 0
              : user.cod_etapa_sub
          : 0)
          ? maxMadCodEtapaSub
          : typeof user.cod_etapa_sub === 'number'
            ? user.cod_etapa_sub
            : 0;

      // NIV-04 (2026-07-23): o sub vence se estritamente maior — substituição oficial
      // vale por si, sem gate de mad/férias (senão o substituto não-MAD cod 1/sub 4
      // era tratado como Nível 1 na timeline e nas regras).
      return (codEtapaSub === 1 ? 3 : codEtapaSub) > (codEtapa === 1 ? 3 : codEtapa) ? codEtapaSub : codEtapa;
    },
    [isMAD, playerInfo],
  );

  const points = useMemo(() => {
    if (
      !data ||
      typeof data.multiplicador !== 'number' ||
      data.multiplicador <= 0 ||
      typeof data.status !== 'string' ||
      typeof data.stageNumber !== 'number' ||
      data.stageNumber < 1 ||
      data.stageNumber > 3
    ) {
      return 0;
    }

    let adicional = 0;

    if (
      ((data.status === 'promovido' || data.status === 'concluido') &&
        ((data.stageNumber === 2 && data.multiplicador === 2) || (data.stageNumber === 3 && data.multiplicador === 4))) ||
      (data.stageNumber === 3 && data.multiplicador === 8)
    ) {
      adicional = 100;
    }

    if (data.stageNumber === 3 && data.multiplicador === 16) {
      adicional = 200;
    }

    return data.multiplicador * 300 + adicional;
  }, [data]);

  const realStage = useMemo(() => {
    if (!realCycle || !Array.isArray(realCycle) || !realCycle.length || !data) {
      return null;
    }

    return realCycle
      .find(item => data.created >= item.date_start && data.created <= item.date_end)
      ?.steps?.find(item => data.created >= item.date_start && data.created <= item.date_finish);
  }, [data, realCycle]);

  const realCycleItem: any = useMemo(() => {
    if (!realCycle || !Array.isArray(realCycle) || !realCycle.length || !data) {
      return null;
    }

    return realCycle.find(item => data.created >= item.date_start && data.created <= item.date_end);
  }, [data, realCycle]);

  // Exclusão pelo cadastrante (spec exclusao-realizacao-action-log): ciclo e fase são os da realização,
  // nunca a prop `cycle`, que é o step escolhido na tela.
  const canDelete = !ecoa && canDeleteTask({ task: data, userId: user?._id, taskCycle: realCycleItem, isView, manager });
  const deleteNeedsReason = deleteRequiresReason({ task: data, taskPhase: realStage });

  const hasFeedback = useMemo(
    () =>
      !!Array.isArray(data.feedback) &&
      !!data?.feedback?.find((x: { evaluatorId: any }) => !!realPlayerInfo && x.evaluatorId === realPlayerInfo?.chave),
    [realPlayerInfo, data],
  );

  const getUserTeam = useCallback(
    (_id: string) => {
      try {
        return data?.team?.find((item: any) => item._id === _id);
      } catch (e) {
        return undefined;
      }
    },
    [data?.team],
  );

  const verifyFeedback = useCallback(() => {
    if (
      data &&
      realPlayerInfo?.chave &&
      (!Array.isArray(data.feedback) || !data.feedback.find((x: { evaluatorId: any }) => x.evaluatorId === realPlayerInfo.chave))
    ) {
      closeModal();
      setTimeout(() => {
        showModal(`validation-feedback-${data._id}`);
      }, 150);
    }
  }, [closeModal, data, realPlayerInfo, showModal]);

  function actionSucceeded(result: unknown) {
    return result !== false;
  }

  async function handleReset(task: any) {
    if (onReset && !!task && task._id) {
      onReset(task);
    }
  }

  async function handleReload() {
    if (onReload && !!data && data._id) {
      onReload(data._id);
    }
  }

  async function handleAccept() {
    // if (status) return;

    // if (!confirm('Você confirma que deseja validar esta entrega?')) return;

    // const updatedData = {...data, status : "aprovado", team: [...data.team,{_id:user._id}]}
    // await updateTask(updatedData);
    if (onAccept) {
      onAccept(data);
      // setStatus('aprovado');
    }
  }

  async function handlePromote() {
    // if (status) return;

    // if (!confirm('Você confirma que deseja promover esta entrega?')) return;

    if (onPromote) {
      onPromote(data);
      // setStatus('próximo nível');
    }
  }

  async function handleAward(obj: any) {
    if (onAward && obj?.id) {
      const result = await onAward(data, obj.id);
      return actionSucceeded(result);
    }

    return false;
  }

  async function handleDouble() {
    // if (status) return;

    // if (!confirm('Você confirma que deseja dobrar esta entrega?')) return;

    if (onDouble) {
      const result = await onDouble(data);
      if (!actionSucceeded(result)) {
        return false;
      }
      setStatus('dobrado');
      return true;
    }

    return false;
  }

  async function handleReject() {
    // if (status) return;

    // if (!confirm('Você confirma que deseja devolver esta entrega?')) return;

    // const updatedData = {...data, status : "rejeitado"}
    // await updateTask(updatedData);
    if (onReject) {
      onReject(data);
      setStatus('rejeitado');
    }
  }

  /** Exclusão confirmada pela trigger `task_delete`: a tela tira a realização da lista. */
  function handleDeleted() {
    onDelete?.(data._id);
  }

  async function handleStop() {
    if (onStop) {
      const result = await onStop(data);
      if (!actionSucceeded(result)) {
        return false;
      }
      setStatus('concluido');
      return true;
    }

    return false;
  }

  async function handleFeedback(msg: string) {
    if (!msg || !msg.trim().length) return;

    try {
      const payload: any = {
        message: msg.trim(),
        taskId: data._id,
        evaluatorId: realPlayerInfo.chave,
        evaluator_nome: realPlayerInfo.nome,
        evaluator_nome_guerra: realPlayerInfo.nome_guerra,
        evaluator_email: realPlayerInfo.email,
        etapa: activeEvaluationStep,
      };

      if (isVacationManager) {
        payload.original_task_manager = {
          playerId: playerInfo.chave,
          playerName: playerInfo.nome,
        };
      }

      await submitFeedback(payload);
      if (onFeedback) onFeedback(data);
      // setHasFeedback(true);
    } catch (error) {}
  }

  function handleEdit() {
    navigate(data._id);
  }

  function getFormattedDate(value: any) {
    try {
      return format(utcToZonedTime(value, 'GMT'), 'dd/MM/yy');
    } catch (e) {
      return '---';
    }
  }

  const canViewResetButton: boolean = useMemo((): boolean => {
    if (!reset || !!isView || !userCodEtapa() || typeof taskStep !== 'number') return false;

    if (String(data.original_manager) === String(playerInfo._id) && userCodEtapa() === 5 && cycle) {
      return data.status !== 'aguardando';

      // Ajustando IF para contemplar outras funções gerenciais
      // Presidente e vice-presidente
      // if (playerInfo.funcao_localizacao === 3001 || playerInfo.funcao_localizacao === 3002) {
      //   return data.status !== 'aguardando';
      // } else {
      //   // Diretores e gerentes gerais
      //   if (realDatetime < cycle.date_evaluation_step5_start) {
      //     return data.status !== 'aguardando';
      //   } else if (realDatetime <= cycle.date_evaluation_step5_finish) {
      //     return (
      //       data.status === 'dobrado' ||
      //       data.status === 'mandou' ||
      //       data.status === 'valeu' ||
      //       data.status === 'selo' ||
      //       data.status === 'concluido' ||
      //       data.status === 'concluidoAuto'
      //     );
      //   }
      // }
    }

    if (String(data.original_manager) === String(playerInfo._id) && userCodEtapa() === 4 && cycle) {
      if (realDatetime < cycle.date_evaluation_step4_start) {
        return data.status !== 'aguardando';
      } else if (realDatetime <= cycle.date_evaluation_step4_finish) {
        return (
          data.status === 'dobrado' ||
          data.status === 'promovido' ||
          data.status === 'concluido' ||
          data.status === 'concluidoAuto'
        );
      }
    }

    if (!activeEvaluationStep) {
      return false;
    }

    if (String(data.original_manager) === String(playerInfo._id) && userCodEtapa() === 4) {
      if (
        typeof activeEvaluationStep === 'number' &&
        typeof taskStep === 'number' &&
        typeof data.multiplicador === 'number' &&
        (activeEvaluationStep === 2 || (activeEvaluationStep === 4 && taskStep === 4))
      ) {
        return true;
      }

      return false;
    }

    if (
      taskStep === 4 &&
      activeEvaluationStep === 2 &&
      userCodEtapa() === 4 &&
      data.multiplicador === 2 &&
      data.status === 'promovido'
    ) {
      return false;
    }

    // if (
    //   taskStep === 4 &&
    //   activeEvaluationStep === 2 &&
    //   userCodEtapa() < 4 &&
    //   data.multiplicador >= 2 &&
    //   !!data.status.match(/(concluido|concluidoAuto|dobrado)/gi)
    // ) {
    //   return false;
    // }

    if (
      data.status === 'rejeitado' ||
      data.status === 'dobrado' ||
      data.status === 'mandou' ||
      data.status === 'valeu' ||
      data.status === 'selo' ||
      data.status === 'concluido' ||
      data.status === 'concluidoAuto' ||
      taskStep > activeEvaluationStep
    ) {
      return true;
    }

    if (
      typeof data.prosseguir_gst === 'string' &&
      data.prosseguir_gst !== 'next' &&
      (!activeEvaluationStep || activeEvaluationStep < 5)
    ) {
      return true;
    }

    if (typeof data.multiplicador === 'number') {
      if (data.multiplicador === 1 && activeEvaluationStep === 2) {
        return true;
      }

      if (data.status === 'promovido' && data.multiplicador === 2 && activeEvaluationStep === 3 && userCodEtapa() < 4) {
        return true;
      }

      if (data.status === 'promovido' && data.multiplicador === 4 && activeEvaluationStep >= 4 && activeEvaluationStep < 5) {
        return true;
      }

      return false;
    }

    return false;
    // Dep array preservado 1:1 do task-item original (paridade visual/comportamental).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeEvaluationStep,
    cycle,
    data.multiplicador,
    data.original_manager,
    data.prosseguir_gst,
    data.status,
    isView,
    playerInfo._id,
    playerInfo.funcao_localizacao,
    realDatetime,
    reset,
    taskStep,
    userCodEtapa,
  ]);

  const pitStopCanEvaluate = useMemo(() => {
    if (!!isView) return false;

    // MAD (PIT-05): quando a página fornece o gate dupla-aware por prop, ele é a fonte da
    // verdade; caso contrário mantém o cálculo posicional original (fluxo Normal).
    if (typeof isPitStopAssessor === 'boolean') return isPitStopAssessor;

    if (manager && canEvaluate && reset) {
      if (!data.prosseguir_idGestor || !data.prosseguir_gst || data.prosseguir_idGestor !== playerInfo._id) return false;

      if (data.prosseguir_gst === 'next') {
        return true;
      }

      return false;
    } else {
      return false;
    }
  }, [manager, canEvaluate, reset, data, playerInfo, isView, isPitStopAssessor]);

  const getStepLabel = (step: number) => {
    switch (step) {
      case 2:
        return 'Validação';
      case 1:
        return 'Nível 1';
      case 3:
        return 'Nível 1';
      case 4:
        return 'Nível 2';
      case 4.1:
        return 'Prosseguir';
      case 5:
        return 'Nível 3';
      default:
        return '';
    }
  };

  // Matriz de apreciação - Não excluir
  // const userQuizAnswered = useMemo(() => !!data?.answeredQuiz || !!quizAnswered, [data, quizAnswered]);

  function isCurrentEvaluator() {
    if (!playerInfo || !data.evaluator || !data.evaluator.length || !!isView) return false;

    const matriculaEvaluator = parseInt(data.evaluator[data.evaluator.length - 1].replace(/F(0+)?/g, ''));

    const currentId = parseInt(playerInfo._id);

    return matriculaEvaluator === currentId && data.status === 'promovido';
  }

  function callBackActions(obj: { [key: string]: any }) {
    if (!obj?.id) return;

    if (obj.id === 'accept') {
      handleAccept();
      // verifyFeedback();
    } else if (obj.id === 'double') {
      void handleDouble()
        .then(succeeded => {
          if (succeeded) {
            verifyFeedback();
          }
        })
        .catch(() => undefined);
    } else if (obj.id === 'feedback') {
      handleFeedback(obj.message as string);
    } else if (obj.id === 'reject') {
      handleReject();
    } else if (obj.id === 'promote' || obj.id === 'selo') {
      handlePromote();
    } else if (obj.id === 'uau' || obj.id === 'mandou' || obj.id === 'valeu') {
      void handleAward(obj)
        .then(succeeded => {
          if (succeeded) {
            verifyFeedback();
          }
        })
        .catch(() => undefined);
    } else if (obj.id === 'open_feedback') {
      verifyFeedback();
    } else if (obj.id === 'stop') {
      void handleStop()
        .then(succeeded => {
          if (succeeded) {
            verifyFeedback();
          }
        })
        .catch(() => undefined);
    } else if (obj.id === 'reset') {
      handleReset(obj.task);
    } else if (obj.id === 'stop_quiz') {
      // Matriz de apreciação - Não excluir
      // setQuizAnswered(true);
      // handleReload();
    } else if (obj.id === 'reload_task') {
      handleReload();
    }
  }

  // const loadFeedback = useCallback(async () => {
  //   if (!!realPlayerInfo && realPlayerInfo.cod_etapa > 0) {
  //     const hasFeedback = await playerHasFeedback(data._id, realPlayerInfo.chave);
  //     setHasFeedback(hasFeedback);
  //   }
  // }, [realPlayerInfo, data]);

  const stepTask = useMemo(
    () =>
      realCycle
        ?.find(x => data.created > x.date_start && data.created <= x.date_end)
        ?.steps.find(x => data.created > x.date_start && data.created <= x.date_finish),
    [data.created, realCycle],
  );

  const canViewBoxActions = (): boolean => {
    if (manager && canEvaluate && !isView) {
      if (!reset) return false;

      if (award?.uau || award?.mandou || award?.valeu) {
        return true;
      }

      if (!bonus && (data.status === 'concluido' || data.status === 'concluidoAuto')) return false;

      if (!promote && !double && status === 'aprovado') {
        return true;
      }

      if (status === 'rejeitado') {
        return true;
      }

      if (!data.stageNumber && (!status || status === 'aguardando')) {
        return true;
      }

      if (managerType !== 'equipe') {
        if (double) {
          return true;
        }

        if (promote) {
          return true;
        }

        return false;
      }

      return false;
    } else if (
      !!canApproveParticipants &&
      ((data?.participants_approval?.status === 'pending' && canApproveParticipantsInstExpired) ||
        data?.participants_approval?.status === 'approved' ||
        data?.participants_approval?.status === 'rejected')
    ) {
      return true;
    }

    return false;
  };

  const canSendFeedback = (): boolean => {
    if (
      Array.isArray(data.substitute_managers) &&
      !!data.substitute_managers.find((x: { playerId: string | undefined }) => x.playerId === user?._id)
    ) {
      if (!hasFeedback) {
        return true;
      }
    }

    if (
      Array.isArray(data.non_evaluator) &&
      data.non_evaluator.length &&
      data.non_evaluator.find((x: any) => x === playerInfo?._id) &&
      activeEvaluationStep &&
      playerStep &&
      !isView
    ) {
      if (!hasFeedback) {
        return true;
      }
    }

    if (
      !reset ||
      // !activeEvaluationStep ||
      !!isView /*||
      (!!data?.prosseguir_idGestor && data.prosseguir_idGestor === playerInfo._id)*/
    ) {
      return false;
    }

    if (!hasFeedback) {
      return true;
    }

    return false;
  };

  const getEvaluatorData = (item: any) => {
    if (item.evaluator_detail?.length) {
      const evaluator = item.evaluator_detail.find((x: { _id: any }) => x._id === item.evaluator[item.evaluator.length - 1]);

      return !!evaluator ? evaluator : undefined;
    }

    return undefined;
  };

  const evaluationData = useMemo(() => {
    if (stepTask && user?._id === data?.owner && realDatetime > stepTask?.date_evaluation_step2_finish) {
      return (
        <div className="msg-countdown">
          <div>
            <p tabIndex={0}>O período para avaliação da quantidade de participantes desta realização se encerrou.</p>
          </div>
        </div>
      );
    }

    if (!!canApproveParticipants) {
      if (data?.participants_approval?.status === 'pending' && canApproveParticipantsInstExpired) {
        return (
          <div className="msg-countdown actual">
            <div>
              <p tabIndex={0}>
                Esta realização está <strong>pendente</strong> de autorização da quantidade de participantes.
              </p>
            </div>
          </div>
        );
      } else if (data?.participants_approval?.status === 'pending' && !canApproveParticipantsInstExpired) {
        return (
          <div className="msg-countdown">
            <div>
              <p tabIndex={0}>O período para avaliação da quantidade de participantes desta realização se encerrou.</p>
            </div>
          </div>
        );
      } else if (data?.participants_approval?.status === 'approved') {
        return (
          <div className="msg-countdown">
            <div>
              <p tabIndex={0}>
                A quantidade de participantes desta realização foi <strong>autorizada</strong>.
              </p>
            </div>
          </div>
        );
      } else if (data?.participants_approval?.status === 'rejected') {
        return (
          <div className="msg-countdown">
            <div>
              <p tabIndex={0}>
                A quantidade de participantes desta realização <strong>não foi autorizada</strong>.
              </p>
            </div>
          </div>
        );
      }

      return <></>;
    }

    if (!cycle || !activeEvaluationStep || !taskStep || !playerStep || cycle.status !== 'evaluation' || !!isView) return <></>;

    // TEMP
    if (activeEvaluationStep >= 4 && playerStep < 4) return <></>;

    const date_finish =
      activeEvaluationStep === 2
        ? cycle.date_evaluation_step2_finish
        : activeEvaluationStep === 3
          ? cycle.date_evaluation_step3_finish
          : activeEvaluationStep === 4
            ? cycle.date_evaluation_step4_finish
            : activeEvaluationStep === 4.1
              ? cycle.date_evaluation_pitStop_finish
              : cycle.date_evaluation_step5_finish;

    // Se já concluiu a validação
    if (
      !!reset &&
      activeEvaluationStep === 2 &&
      (((playerStep as number) === 2 && (taskStep as number) > 2) ||
        ((playerStep as number) === 3 && (taskStep as number) > 3) ||
        ((playerStep as number) === 4 &&
          (taskStep as number) >= 4 &&
          !!playerInfo.mad &&
          !!data.status.match(/(concluido|concluidoAuto|dobrado)/gi)))
    ) {
      return (
        <div className="msg-countdown">
          <div>
            <p tabIndex={0}>Você concluiu a apreciação dessa realização.</p>
          </div>
        </div>
      );
    }

    // Se já terminou o prazo e concluiu a validação
    else if (!reset && (playerStep as number) < activeEvaluationStep && (taskStep as number) > (playerStep as number)) {
      return (
        <div className="msg-countdown">
          <div>
            <p tabIndex={0}>Você concluiu a apreciação dessa realização.</p>
          </div>
        </div>
      );
    }

    // Se já terminou o prazo e não concluiu a validação
    else if (!reset && (playerStep as number) < activeEvaluationStep && (taskStep as number) <= (playerStep as number)) {
      return (
        <div className="msg-countdown">
          <div>
            <p tabIndex={0}>O período para apreciação dessa realização se encerrou.</p>
          </div>
        </div>
      );
    }

    // Se está na etapa atual
    else if (
      !!reset &&
      (((playerStep as number) === 2 && activeEvaluationStep === 2) ||
        ((playerStep as number) === 3 && activeEvaluationStep === 3) ||
        ((playerStep as number) === 4 && activeEvaluationStep === 4) ||
        ((playerStep as number) === 4.1 && activeEvaluationStep === 4.1) ||
        ((playerStep as number) === 5 && activeEvaluationStep === 5))
    ) {
      // A apreciação ocorre normalmente mesmo com a quantidade de participantes pendente/não
      // aprovada — o aviso de pendência é exibido na caixa de apreciação. Aqui mantemos a
      // contagem regressiva do prazo de apreciação.
      return (
        <div className="msg-countdown actual">
          <div>
            <p tabIndex={0}>
              O período para apreciação dessa realização termina em:{' '}
              <strong className="whitespace-nowrap">
                <Countdown deadline={new Date(date_finish)} />
              </strong>
            </p>
          </div>
        </div>
      );
    }

    // Ele é etapa posterior e o período vai chegar
    else if ((playerStep as number) > activeEvaluationStep && (taskStep as number) > activeEvaluationStep) {
      const date_start_evaluation =
        playerStep === 2
          ? cycle.date_evaluation_step2_start
          : playerStep === 3
            ? cycle.date_evaluation_step3_start
            : playerStep === 4
              ? cycle.date_evaluation_step4_start
              : playerStep === 4.1
                ? cycle.date_evaluation_pitStop_start
                : playerStep === 5
                  ? cycle.date_evaluation_step5_start
                  : null;

      return (
        <div className="msg-countdown">
          <div>
            <p tabIndex={0}>
              O período para apreciação dessa realização inicia em
              {!!date_start_evaluation ? (
                <>
                  :{' '}
                  <strong className="whitespace-nowrap">
                    <Countdown deadline={new Date(date_start_evaluation)} />
                  </strong>
                </>
              ) : (
                ' breve.'
              )}
            </p>
          </div>
        </div>
      );
    }
  }, [
    stepTask,
    user?._id,
    data?.owner,
    data.status,
    data?.participants_approval?.status,
    realDatetime,
    canApproveParticipants,
    cycle,
    activeEvaluationStep,
    taskStep,
    playerStep,
    isView,
    reset,
    playerInfo.mad,
    canApproveParticipantsInstExpired,
  ]);

  const activeStage = useMemo(() => {
    if (!playerInfo || !taskStep) {
      return 0;
    }

    if (taskStep === 2) {
      return 0;
    } else if (taskStep === 3) {
      return 1;
    } else if (taskStep === 4) {
      return 2;
    } else if (taskStep === 4.1 || taskStep === 5) {
      return 3;
    } else {
      return 0;
    }
  }, [playerInfo, taskStep]);

  const getManagerName = useCallback(() => {
    if (!taskStep) return;

    const dataEvaluatorDetail = [...(Array.isArray(data.evaluator) && data.evaluator.length ? data.evaluator : [])]
      .filter(x => !!x && !!x.match(/^\d+$/gi))
      .map(x => data?.evaluator_detail?.find((e: any) => e._id === x));

    // Os marcadores de pit stop (prosseguir_idGestor/prosseguir_managers) podem apontar para
    // QUALQUER membro da dupla do assessor enquanto `evaluator` carrega o outro (dupla cruzada/
    // dados legados) — a exclusão da timeline precisa cobrir a dupla inteira, como a escrita
    // (isPitStopAssessor) já cobre.
    const prosseguirIds = new Set(
      [data?.prosseguir_idGestor, ...(Array.isArray(data?.prosseguir_managers) ? data.prosseguir_managers : [])].filter(
        Boolean,
      ),
    );
    const isProsseguirParty = (x: any) =>
      !!x &&
      (prosseguirIds.has(x._id) ||
        (Array.isArray(x.mad_dupla) && x.mad_dupla.some((md: any) => prosseguirIds.has(md?._id))));

    // Pools por nível com FALLBACK pelo cod_etapa PRINCIPAL (2026-07-23): o substituto
    // (ex.: cod 1 / sub 4) tem o nível EFETIVO elevado pelo sub, mas pode ter agido pelo
    // nível da função própria. Preferência a quem casa o nível pelo cod efetivo; se
    // NINGUÉM casa, cai para o cod_etapa cru — o elo que agiu não some da timeline.
    const levelPool = (effective: (x: any) => boolean, raw: (x: any) => boolean) => {
      const elegiveis = dataEvaluatorDetail.filter(x => !!x && !isProsseguirParty(x));
      const porEfetivo = elegiveis.filter(effective);

      return porEfetivo.length ? porEfetivo : elegiveis.filter(raw);
    };
    const poolLvl1 = () =>
      levelPool(
        x => userCodEtapa(x) === 1 || userCodEtapa(x) === 3,
        x => x.cod_etapa === 1 || x.cod_etapa === 3,
      );
    const poolLvl2 = () => levelPool(x => userCodEtapa(x) === 4, x => x.cod_etapa === 4);

    const evaluators: any = {
      lvl_0: data?.original_manager_detail,
      lvl_1: null,
      lvl_2: null,
      lvl_3: null,
    };

    if (
      !!userCodEtapa(data?.original_manager_detail) &&
      userCodEtapa(data?.original_manager_detail) >= 1 &&
      userCodEtapa(data?.original_manager_detail) <= 5 &&
      userCodEtapa(data?.original_manager_detail) !== 2
    ) {
      evaluators['lvl_1'] = data.original_manager_detail;
      evaluators['lvl_2'] =
        userCodEtapa(data?.original_manager_detail) === 4 || userCodEtapa(data?.original_manager_detail) === 5
          ? data.original_manager_detail
          : null;
      evaluators['lvl_3'] = userCodEtapa(data?.original_manager_detail) === 5 ? data.original_manager_detail : null;
    }

    if (dataEvaluatorDetail.length) {
      if (taskStep === 3 && evaluators['lvl_1'] === null) {
        const managersLvl1 = poolLvl1();

        if (managersLvl1.length) evaluators['lvl_1'] = managersLvl1[managersLvl1.length - 1];
      } else if (taskStep === 4 && evaluators['lvl_2'] === null) {
        const managersLvl1 = poolLvl1();
        const managersLvl2 = poolLvl2();

        if (evaluators['lvl_1'] === null && managersLvl1.length) evaluators['lvl_1'] = managersLvl1[managersLvl1.length - 1];
        if (managersLvl2.length) evaluators['lvl_2'] = managersLvl2[managersLvl2.length - 1];
      } else if (taskStep === 4.1) {
        const managersLvl1 = poolLvl1();
        const managersLvl2 = poolLvl2();

        if (evaluators['lvl_1'] === null && managersLvl1.length) evaluators['lvl_1'] = managersLvl1[managersLvl1.length - 1];
        if (evaluators['lvl_2'] === null && managersLvl2.length) evaluators['lvl_2'] = managersLvl2[managersLvl2.length - 1];
        evaluators['lvl_3'] =
          dataEvaluatorDetail.find(x => isProsseguirParty(x)) || null;
      } else if (taskStep === 5 && evaluators['lvl_3'] === null) {
        const managersLvl1 = poolLvl1();
        const managersLvl2 = poolLvl2();
        const managersLvl3 = dataEvaluatorDetail.filter(
          x =>
            userCodEtapa(x) === 5 &&
            !isProsseguirParty(x),
        );

        if (evaluators['lvl_1'] === null && managersLvl1.length) evaluators['lvl_1'] = managersLvl1[managersLvl1.length - 1];
        if (evaluators['lvl_2'] === null && managersLvl2.length) evaluators['lvl_2'] = managersLvl2[managersLvl2.length - 1];
        if (managersLvl3.length) evaluators['lvl_3'] = managersLvl3[managersLvl3.length - 1];
      }
    }

    return evaluators;
  }, [data, taskStep, userCodEtapa]);

  const getManagerMAD = useCallback(
    (manager: any) => {
      if (
        Array.isArray(data.non_evaluator_detail) &&
        data.non_evaluator_detail.length &&
        Array.isArray(manager?.mad_dupla) &&
        manager.mad_dupla.length
      ) {
        return data.non_evaluator_detail.find((x: any) => manager.mad_dupla.some((md: any) => md._id === x._id));
      }
      return undefined;
    },
    [data],
  );

  const managersByStep: managersByStepProps[] | undefined = useMemo(() => {
    if (!data || /*!data.gestores ||*/ typeof taskStep !== 'number' || !cycle) return undefined;

    // const dataGestores = {
    //   gestor1: {
    //     chave: data.gestores.chave,
    //     nome_guerra: data.gestores.nome_guerra,
    //     cod_etapa: data.gestores.cod_etapa,
    //     prosseguir_gst: data.gestores.prosseguir_gst,
    //   },
    //   ...data.gestores,
    // };
    // const managersKeys = Object.keys(dataGestores).filter(x => !!x.match(/gestor/g));
    // const managers: managersByStepProps[] = [];

    // managersKeys.forEach((key, index) => {
    //   const manager = dataGestores[key];
    //   const obj = { ...manager };

    //   // Etapa
    //   if (managers.length && !!managers[index - 1] && managers[index - 1].prosseguir_gst === 1 && obj.cod_etapa !== 5) {
    //     return;
    //   } else {
    //     if (obj.cod_etapa === 2) {
    //       obj.stageTitle = 'Validação';
    //       obj.stage = 'stage0';
    //       obj.stageNumber = 0;
    //       obj.blocks = 1;
    //     } else if (obj.cod_etapa === 3 || obj.cod_etapa === 1) {
    //       if (managers.length) {
    //         obj.stageTitle = 'Nível 1';
    //         obj.stage = 'stage1';
    //         obj.stageNumber = 1;
    //         obj.blocks = 2;
    //       } else {
    //         obj.stageTitle = `Validação`;
    //         obj.stage = 'stage0';
    //         obj.stageNumber = 0;
    //         obj.blocks = 1;
    //       }
    //     } else if (obj.cod_etapa === 4) {
    //       obj.stageTitle = 'Nível 2';
    //       obj.stage = 'stage2';
    //       obj.stageNumber = 2;
    //       obj.blocks = 2;
    //     } else if (obj.cod_etapa === 5) {
    //       obj.stageTitle = 'Nível 3';
    //       obj.stage = 'stage3';
    //       obj.stageNumber = 3;
    //       obj.blocks = 2;
    //     }
    //   }

    //   if (obj.cod_etapa === 5) {
    //     const hasStep5 = !!managers.length && !!managers.find((x: { cod_etapa?: number }) => x.cod_etapa && x.cod_etapa === 5);

    //     if (!hasStep5) {
    //       managers.push(obj);
    //     }
    //   } else {
    //     managers.push(obj);
    //   }
    // });

    // const managerStep0 = managers.find((x: { stageNumber: number }) => x.stageNumber === 0);

    // if (
    //   !!managerStep0 &&
    //   (managerStep0.cod_etapa === 3 || managerStep0.cod_etapa === 1) &&
    //   !managers.find((x: { stageNumber: number }) => x.stageNumber === 1)
    // ) {
    //   managers.push({
    //     ...managerStep0,
    //     stageTitle: 'Nível 1',
    //     stage: 'stage1',
    //     stageNumber: 1,
    //     blocks: 1,
    //   });
    // }

    // managers.sort((a: any, b: any) => a.stageNumber - b.stageNumber);

    // return managers;

    // Deixando estático
    return [
      {
        stageTitle: 'Validação',
        stage: 'stage0',
        stageNumber: 0,
        blocks: 1,
      },
      {
        stageTitle: 'Nível 1',
        stage: 'stage1',
        stageNumber: 1,
        blocks: 1,
      },
      {
        stageTitle: 'Nível 2',
        stage: 'stage2',
        stageNumber: 2,
        blocks: 2,
      },
      {
        stageTitle: 'Nível 3',
        stage: 'stage3',
        stageNumber: 3,
        blocks: 2,
      },
    ];
  }, [data, taskStep, cycle]);

  const timelineObj: any = useMemo(() => {
    if (!data || !realCycle || !taskStep) return undefined;

    const actualTime = +new Date();

    if (!stepTask) return undefined;

    const stage0: any = { opened: true, titleColor: '#FFFFFF', bgColor: '#C7D3EB' },
      stage1: any = { opened: false, titleColor: '#597B95', bgColor1: '#C7D3EB', bgColor2: '#C7D3EB' },
      stage2: any = { opened: false, titleColor: '#597B95', bgColor1: '#C7D3EB', bgColor2: '#C7D3EB' },
      stage3: any = { opened: false, titleColor: '#597B95', bgColor1: '#C7D3EB', bgColor2: '#C7D3EB' };

    // Gerente de equipe
    if (data.status === 'aguardando' && actualTime <= stepTask.date_evaluation_step2_finish) {
      stage0.status = 'Aguardando validação';
      stage0.statusColor = '#FFFFFF';
      stage0.gestor = getManagerName().lvl_0;
    } else if (data.status === 'aguardando' && actualTime > stepTask.date_evaluation_step2_finish) {
      stage0.status = 'Prazo expirado';
      stage0.statusColor = '#FDC81C';
      stage0.bgColor = '#FDC81C';
      stage0.gestor = getManagerName().lvl_0;
    } else if (data.status === 'rejeitado') {
      stage0.status = 'Devolvida';
      stage0.statusColor = '#ED2B48';
      stage0.bgColor = '#ED2B48';

      if (Array.isArray(data.feedback) && !!data.feedback.find((x: { evaluatorId: string }) => x.evaluatorId === 'F0000000')) {
        // stage0.gestor = null;
      } else {
        stage0.gestor = { ...getManagerName().lvl_0, changeColor: true };
      }
    } else if (data.multiplicador && data.multiplicador >= 1) {
      stage0.status = 'Validada';
      stage0.statusColor = '#04D475';
      stage0.bgColor = '#04D475';
      stage0.gestor = { ...getManagerName().lvl_0, changeColor: true };
    }

    if (data.status === 'rejeitado' || (data.multiplicador && data.multiplicador >= 1)) {
      if (Array.isArray(data.feedback) && !!data.feedback.find((x: { evaluatorId: string }) => x.evaluatorId === 'F0000000')) {
        // stage0.gestor = null;
      } else {
        stage0.gestor = { ...getManagerName().lvl_0, changeColor: true };
      }
    }

    if (ecoa) {
      stage0.status = 'Aguardando validação';
      stage0.statusColor = '#FFFFFF';
      stage0.gestor = getManagerName().lvl_0;
    }

    // Nível 1
    if (taskStep > 2) {
      stage1.opened = true;
      stage1.titleColor = '#FFFFFF';

      if (
        data.status === 'aprovado' &&
        data.multiplicador &&
        data.multiplicador === 1 &&
        actualTime <= stepTask.date_evaluation_step3_finish
      ) {
        stage1.status = 'Aguardando apreciação';
        stage1.statusColor = '#FFFFFF';
        stage1.gestor = getManagerName().lvl_1;
      } else if (
        data.status === 'aprovado' &&
        data.multiplicador &&
        data.multiplicador === 1 &&
        actualTime > stepTask.date_evaluation_step3_finish
      ) {
        stage1.status = 'Prazo expirado';
        stage1.statusColor = '#FDC81C';
        stage1.bgColor1 = '#FDC81C';
        stage1.bgColor2 = '#FDC81C';
        stage1.gestor = getManagerName().lvl_1;
      } else if (data.status === 'concluido' && data.multiplicador && data.multiplicador === 1 && taskStep === 3) {
        stage1.status = 'Validada - Finalizada';
        stage1.statusColor = '#7CFFEB';
        stage1.gestor = { ...getManagerName().lvl_1, changeColor: true };
      } else if (
        data.status === 'dobrado' &&
        data.multiplicador &&
        data.multiplicador === 2 &&
        actualTime <= stepTask.date_evaluation_step3_finish
      ) {
        stage1.status = 'Bonificada';
        stage1.statusColor = '#04D475';
        stage1.bgColor1 = '#04D475';
        stage1.gestor = { ...getManagerName().lvl_1, changeColor: true };
      } else if (
        data.status === 'dobrado' &&
        data.multiplicador &&
        data.multiplicador === 2 &&
        actualTime > stepTask.date_evaluation_step3_finish
      ) {
        stage1.status = 'Bonificada - Prazo expirado';
        stage1.statusColor = '#04D475';
        stage1.bgColor1 = '#04D475';
        stage1.bgColor2 = '#FDC81C';
        stage1.gestor = { ...getManagerName().lvl_1, changeColor: true };
      } else if (
        (data.status === 'concluido' || data.status === 'concluidoAuto') &&
        data.multiplicador &&
        data.multiplicador === 2 &&
        taskStep === 3
      ) {
        stage1.status = 'Bonificada - Finalizada';
        stage1.statusColor = '#04D475';
        stage1.bgColor1 = '#04D475';
        stage1.gestor = { ...getManagerName().lvl_1, changeColor: true };
      } else if (data.multiplicador && data.multiplicador >= 2 && taskStep > 3) {
        stage1.status = 'Promovida';
        stage1.statusColor = '#04D475';
        stage1.bgColor1 = '#04D475';
        stage1.bgColor2 = '#04D475';
        stage1.gestor = { ...getManagerName().lvl_1, changeColor: true };
      }
    }

    // Nível 2
    if (taskStep > 3) {
      stage2.opened = true;
      stage2.titleColor = '#FFFFFF';

      // if (
      //   !!data.mad &&
      //   data.status === 'promovido' &&
      //   data.multiplicador &&
      //   data.multiplicador === 2 &&
      //   stepTask &&
      //   stepTask.date_evaluation_step3_finish &&
      //   actualTime > stepTask.date_evaluation_step3_finish
      // ) {
      //   stage2.status = 'Prazo expirado';
      //   stage2.statusColor = '#FDC81C';
      //   stage2.bgColor1 = '#FDC81C';
      //   stage2.gestor = getManagerName().lvl_2;
      // }

      if (
        data.status === 'promovido' &&
        data.multiplicador &&
        data.multiplicador === 2 &&
        (!stepTask.date_evaluation_step4_finish || actualTime <= stepTask.date_evaluation_step4_finish)
      ) {
        stage2.status = 'Aguardando apreciação';
        stage2.statusColor = '#FFFFFF';
        stage2.gestor = getManagerName().lvl_2;
      } else if (
        stepTask.date_evaluation_step4_finish &&
        data.status === 'promovido' &&
        data.multiplicador &&
        data.multiplicador === 2 &&
        actualTime > stepTask.date_evaluation_step4_finish
      ) {
        stage2.status = 'Prazo expirado';
        stage2.statusColor = '#FDC81C';
        stage2.bgColor1 = '#FDC81C';
        stage2.bgColor2 = '#FDC81C';
        stage2.gestor = getManagerName().lvl_2;
      } else if (data.status === 'concluido' && data.multiplicador && data.multiplicador === 2 && taskStep === 4) {
        stage2.status = 'Promovida ao nível 2 - Finalizada';
        stage2.statusColor = '#7CFFEB';
        stage2.gestor = { ...getManagerName().lvl_2, changeColor: true };
      } else if (
        data.status === 'dobrado' &&
        data.multiplicador &&
        data.multiplicador === 4 &&
        (!stepTask.date_evaluation_step4_finish || actualTime <= stepTask.date_evaluation_step4_finish)
      ) {
        stage2.status = 'Bonificada';
        stage2.statusColor = '#04D475';
        stage2.bgColor1 = '#04D475';
        stage2.gestor = { ...getManagerName().lvl_2, changeColor: true };
      } else if (
        data.status === 'dobrado' &&
        data.multiplicador &&
        data.multiplicador === 4 &&
        actualTime > stepTask.date_evaluation_step4_finish
      ) {
        stage2.status = 'Bonificada - Prazo expirado';
        stage2.statusColor = '#04D475';
        stage2.bgColor1 = '#04D475';
        stage2.bgColor2 = '#FDC81C';
        stage2.gestor = { ...getManagerName().lvl_2, changeColor: true };
      } else if (
        (data.status === 'concluido' || data.status === 'concluidoAuto') &&
        data.multiplicador &&
        data.multiplicador === 4 &&
        taskStep === 4
      ) {
        stage2.status = 'Bonificada - Finalizada';
        stage2.statusColor = '#04D475';
        stage2.bgColor1 = '#04D475';
        stage2.gestor = { ...getManagerName().lvl_2, changeColor: true };
      } else if (data.multiplicador && data.multiplicador >= 4 && taskStep > 4) {
        stage2.status = 'Promovida';
        stage2.statusColor = '#04D475';
        stage2.bgColor1 = '#04D475';
        stage2.bgColor2 = '#04D475';
        stage2.gestor = { ...getManagerName().lvl_2, changeColor: true };
      }
    }

    // Nível 3
    if (taskStep > 4) {
      stage3.opened = true;
      stage3.titleColor = '#FFFFFF';

      if (
        data.status === 'promovido' &&
        data.multiplicador &&
        data.multiplicador === 4 &&
        (!stepTask.date_evaluation_step5_finish || actualTime <= stepTask.date_evaluation_step5_finish)
      ) {
        stage3.status = 'Aguardando apreciação';
        stage3.statusColor = '#FFFFFF';
        stage3.gestor = getManagerName().lvl_3;
      } else if (
        stepTask.date_evaluation_step5_finish &&
        data.status === 'promovido' &&
        data.multiplicador &&
        data.multiplicador === 4 &&
        actualTime > stepTask.date_evaluation_step5_finish
      ) {
        stage3.status = 'Prazo expirado';
        stage3.statusColor = '#FDC81C';
        stage3.bgColor1 = '#FDC81C';
        stage3.bgColor2 = '#FDC81C';
        stage3.gestor = getManagerName().lvl_3;
      } else if (data.status === 'concluido' && data.multiplicador && data.multiplicador === 4) {
        stage3.status = 'Promovida ao nível 3 - Finalizada';
        stage3.statusColor = '#7CFFEB';
        stage3.gestor = { ...getManagerName().lvl_3, changeColor: true };
      } else if (
        stepTask.date_evaluation_step5_finish &&
        data.status === 'dobrado' &&
        data.multiplicador &&
        data.multiplicador === 8 &&
        actualTime <= stepTask.date_evaluation_step5_finish
      ) {
        stage3.status = 'Bonificada';
        stage3.statusColor = '#04D475';
        stage3.bgColor1 = '#04D475';
        stage3.gestor = { ...getManagerName().lvl_3, changeColor: true };
      } else if (
        stepTask.date_evaluation_step5_finish &&
        data.status === 'dobrado' &&
        data.multiplicador &&
        data.multiplicador === 8 &&
        actualTime > stepTask.date_evaluation_step5_finish
      ) {
        stage3.status = 'Bonificada - Prazo expirado';
        stage3.statusColor = '#04D475';
        stage3.bgColor1 = '#04D475';
        stage3.bgColor2 = '#FDC81C';
        stage3.gestor = { ...getManagerName().lvl_3, changeColor: true };
      } else if (
        (data.status === 'concluido' || data.status === 'concluidoAuto') &&
        data.multiplicador &&
        data.multiplicador === 8
      ) {
        stage3.status = 'Bonificada - Finalizada';
        stage3.statusColor = '#04D475';
        stage3.bgColor1 = '#04D475';
        stage3.gestor = { ...getManagerName().lvl_3, changeColor: true };
      } else if (data.status === 'selo' || data.status === 'mandou' || data.status === 'valeu') {
        stage3.status = `${data.status === 'selo' && points >= 5000 ? 'Bonificada UAU' : data.status === 'mandou' ? 'Bonificada Mandou Bem' : data.status === 'valeu' ? 'Bonificada Valeu' : 'Premiada'} - Finalizada`;
        stage3.statusColor = '#04D475';
        stage3.bgColor1 = '#04D475';
        stage3.bgColor2 = '#04D475';
        stage3.gestor = { ...getManagerName().lvl_3, changeColor: true };
      }
    }

    if (!!userCodEtapa(data.original_manager_detail) && userCodEtapa(data.original_manager_detail) === 5) {
      if (
        data.status === 'aguardando' &&
        actualTime <= stepTask.date_evaluation_step5_finish
        // Ajustando IF para contemplar outras funções gerenciais
        // (((data.original_manager_detail?.cod_funcao === 3001 || data.original_manager_detail?.cod_funcao === 3002) &&
        //   actualTime <= stepTask.date_evaluation_step5_finish) ||
        //   (data.original_manager_detail?.cod_funcao !== 3001 &&
        //     data.original_manager_detail?.cod_funcao !== 3002 &&
        //     actualTime < stepTask.date_evaluation_step5_start))
      ) {
        stage0.status = 'Aguardando validação';
        stage0.statusColor = '#FFFFFF';
        stage0.bgColor = '#C7D3EB';
      }

      if (
        taskStep > 2 &&
        data.status === 'aprovado' &&
        data.multiplicador &&
        data.multiplicador === 1 &&
        actualTime <= stepTask.date_evaluation_step5_finish
        // Ajustando IF para contemplar outras funções gerenciais
        // (((data.original_manager_detail?.cod_funcao === 3001 || data.original_manager_detail?.cod_funcao === 3002) &&
        //   actualTime <= stepTask.date_evaluation_step5_finish) ||
        //   (data.original_manager_detail?.cod_funcao !== 3001 &&
        //     data.original_manager_detail?.cod_funcao !== 3002 &&
        //     actualTime < stepTask.date_evaluation_step5_start))
      ) {
        stage1.opened = true;
        stage1.titleColor = '#FFFFFF';
        stage1.status = 'Aguardando apreciação';
        stage1.statusColor = '#FFFFFF';
        stage1.bgColor1 = '#C7D3EB';
        stage1.bgColor2 = '#C7D3EB';
      }
    }

    if (!!userCodEtapa(data.original_manager_detail) && userCodEtapa(data.original_manager_detail) === 4) {
      if (
        data.status === 'aguardando' &&
        stepTask.date_evaluation_step4_start &&
        actualTime <= stepTask.date_evaluation_step4_start
      ) {
        stage0.status = 'Aguardando validação';
        stage0.statusColor = '#FFFFFF';
        stage0.bgColor = '#C7D3EB';
      }

      if (
        taskStep > 2 &&
        data.status === 'aprovado' &&
        data.multiplicador &&
        data.multiplicador === 1 &&
        stepTask.date_evaluation_step4_start &&
        actualTime <= stepTask.date_evaluation_step4_start
      ) {
        stage1.opened = true;
        stage1.titleColor = '#FFFFFF';
        stage1.status = 'Aguardando apreciação';
        stage1.statusColor = '#FFFFFF';
        stage1.bgColor1 = '#C7D3EB';
        stage1.bgColor2 = '#C7D3EB';
      }
    }

    return { stage0, stage1, stage2, stage3 };
  }, [data, ecoa, getManagerName, points, realCycle, stepTask, taskStep, userCodEtapa]);

  const timeline = useCallback(
    (manager: managersByStepProps) => {
      if (!!manager?.stage && !!timelineObj && !!timelineObj[manager?.stage]) return timelineObj[manager.stage];
      else return undefined;
    },
    [timelineObj],
  );

  const doubleByPassCod5: boolean = useMemo(() => {
    if (String(data.original_manager) === String(playerInfo._id) && userCodEtapa() === 5) {
      return true;

      // Ajustando IF para contemplar outras funções gerenciais
      // Presidente e vice-presidente
      // if (playerInfo.funcao_localizacao === 3001 || playerInfo.funcao_localizacao === 3002) {
      //   return true;
      // } else {
      //   // Diretores e gerentes gerais
      //   if (activeEvaluationStep && activeEvaluationStep < 5) {
      //     return true;
      //   } else {
      //     return false;
      //   }
      // }
    }

    return false;
    // Dep array preservado 1:1 do task-item original (paridade visual/comportamental).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEvaluationStep, data, playerInfo, userCodEtapa]);

  const canDecreaseParticipation = useCallback(
    (member: any) => {
      const gestor = !!member?.gestor ? `F${member?.gestor?.padStart(7, '0')}` : member._id;

      if (
        realCycleItem &&
        realCycleItem?.date_evaluation_end > realDatetime &&
        (user?._id === member._id || user?._id === gestor) &&
        !!getUserTeam(member._id)?.percent &&
        getUserTeam(member._id).percent > 10 &&
        member._id !== data.owner &&
        !managerType
      ) {
        return true;
      }

      return false;
    },
    [data, user?._id, getUserTeam, managerType, realCycleItem, realDatetime],
  );

  const canExcludeParticipation = useCallback(
    (member: any) => {
      // Regra antiga
      // if (
      //   cycle &&
      //   cycle.status === 'open' &&
      //   data.now &&
      //   data.now < cycle.date_evaluation_step2_start &&
      //   user?._id === member._id &&
      //   user?._id !== data.owner &&
      //   (data.status === 'aguardando' || data.status === 'rejeitado')
      // ) {
      //   return true;
      // }

      const gestor = !!member?.gestor ? `F${member?.gestor?.padStart(7, '0')}` : member._id;

      if (
        realCycleItem &&
        realCycleItem?.date_evaluation_end > realDatetime &&
        (user?._id === member._id || user?._id === gestor) &&
        member._id !== data.owner &&
        !managerType
      ) {
        return true;
      }

      return false;
    },
    [realCycleItem, realDatetime, user?._id, data.owner, managerType],
  );

  const isParticipantManager = useCallback(
    (member: any) => {
      if (!member?.gestor || !user) {
        return false;
      }

      const gestor = `F${member?.gestor?.padStart(7, '0')}`;

      return user?._id === gestor;
    },
    [user],
  );

  // const canViewBoxApproveTeamMember = useCallback(
  //   (targetMember: any) => {
  //     if (targetMember?.gestor && playerInfo?._id && targetMember.gestor === playerInfo?._id) {
  //       const teamMember = data.team.find((x: { _id: any }) => x._id === targetMember._id);

  //       if (!!teamMember && !!teamMember.requiresApproval && typeof teamMember.approvedByManager === 'undefined') {
  //         return true;
  //       } else {
  //         return false;
  //       }
  //     }

  //     return false;
  //   },
  //   [data, playerInfo],
  // );

  // const showMessageRequiresApproval = useCallback(
  //   (targetMember: any) => {
  //     if (targetMember?.gestor && playerInfo?._id && targetMember.gestor === playerInfo?._id) return false;

  //     const teamMember = data.team.find((x: { _id: any }) => x._id === targetMember._id);

  //     if (!!teamMember && !!teamMember.requiresApproval && typeof teamMember.approvedByManager === 'undefined') {
  //       return true;
  //     } else {
  //       return false;
  //     }
  //   },
  //   [data, playerInfo],
  // );

  const questionTitle = useMemo(() => {
    if (pitStopCanEvaluate) {
      return 'Deseja que essa realização prossiga para o último nível de apreciação (Diretor/Gerente Geral UE)?';
    }

    if (award?.uau || award?.mandou || award?.valeu) {
      return 'O que deseja fazer com essa entrega?';
    }

    if (
      (!promote && !double && status === 'aprovado') ||
      status === 'rejeitado' ||
      (!data.stageNumber && (!status || status === 'aguardando'))
    ) {
      return 'O que deseja fazer com essa entrega?';
    } else if (managerType && managerType !== 'equipe' && (promote || double)) {
      return 'O que deseja fazer com essa entrega?';

      // if (double) {
      //   // Matriz de apreciação - Não excluir
      //   // if (!userQuizAnswered && playerStep !== 5) {
      //   //   return (
      //   //     <>
      //   //       Uma matriz de orientação foi elaborada para auxiliar na apreciação das entrega. <br />
      //   //       Deseja utilizá-la?
      //   //     </>
      //   //   );
      //   // } else {
      //   return 'Deseja bonificar essa entrega?';
      //   // }
      // } else if (promote) {
      //   if (managerType === 'diretoria') {
      //     return 'Deseja premiar essa entrega com Selo Realização Destaque?';
      //   } else {
      //     return 'Deseja promover essa entrega?';
      //   }
      // } else {
      //   return 'Deseja enviar esta entrega para apreciação do Nível 3?';
      // }
    }

    return null;
  }, [award?.mandou, award?.uau, award?.valeu, data.stageNumber, double, managerType, pitStopCanEvaluate, promote, status]);

  // useEffect(() => {
  //   data?.team?.forEach((player: any) => {
  //     if (canExcludeParticipation(player) || canDecreaseParticipation(player)) {
  //       setOppened(true);
  //     }
  //   });
  // }, [canExcludeParticipation, canDecreaseParticipation, data]);

  // useEffect(() => {
  //   data?.target?.forEach((player: any) => {
  //     if (canViewBoxApproveTeamMember(player)) {
  //       setOppened(true);
  //     }
  //   });
  // }, [canViewBoxApproveTeamMember, data]);

  useEffect(() => {
    // if (!!playerInfo && playerInfo.cod_etapa > 0) {
    //   loadFeedback();
    // }

    async function loadRealUser() {
      if (isVacationManager) {
        const playerInfoResult = await getPlayerInfo(user?._id);

        if (playerInfoResult) {
          setRealPlayerInfo(playerInfoResult);

          // setHasFeedback(
          //   !!Array.isArray(data.feedback) &&
          //     !!data.feedback.find((x: { evaluatorId: any }) => x.evaluatorId === playerInfoResult.chave),
          // );
        }
      } else {
        setRealPlayerInfo(playerInfo);
      }
    }

    if (data && user) {
      setStatus(data.status);

      if (!realPlayerInfo && !!playerInfo && userCodEtapa() > 0 && isVacationManager) {
        loadRealUser();
      } else if (!realPlayerInfo && !!playerInfo && userCodEtapa() > 0 && !isVacationManager) {
        setRealPlayerInfo(playerInfo);
      }
    }
  }, [data, user, playerInfo, realPlayerInfo, userCodEtapa, isVacationManager]);

  useEffect(() => {
    if (!getAction?._id || !data?._id || data._id !== getAction._id) return;

    if (!!getAction?.fuction) {
      if (clearGetAction) {
        clearGetAction();
      }
      showModal(`${getAction.fuction}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, getAction]);

  useEffect(() => {
    async function openModalFeedback({ detail }: any) {
      if (data && detail && detail._id && detail._id === data?._id) {
        verifyFeedback();
      }
    }

    document.addEventListener('open:modal-feedback', openModalFeedback, false);

    return () => {
      document.removeEventListener('open:modal-feedback', openModalFeedback);
    };
  }, [data, verifyFeedback]);

  useEffect(() => {
    if (!data.team && !data.target) {
      setOppened(false);
    }
  }, [data]);

  function capitalizeWords(str: string) {
    if (!str || str === '') return str;

    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return {
    activeEvaluationStep,
    activeStage,
    award,
    bonus,
    callBackActions,
    canApproveParticipants,
    canApproveParticipantsInstExpired,
    canDecreaseParticipation,
    canExcludeParticipation,
    canSendFeedback,
    canUpdateIndicator,
    canViewBoxActions,
    canViewResetButton,
    capitalizeWords,
    cycle,
    data,
    double,
    doubleByPassCod5,
    ecoa,
    editable,
    evaluationData,
    getFormattedDate,
    getManagerMAD,
    getStepLabel,
    getUserTeam,
    canDelete,
    deleteNeedsReason,
    handleDeleted,
    handleEdit,
    handleReload,
    hasFeedback,
    isMAD,
    isParticipantManager,
    isParticipantsApprover,
    isView,
    managerType,
    managersByStep,
    onDouble,
    onOpenModalSendMessage,
    onUpdateIndicator,
    oppened,
    pitStopCanEvaluate,
    playerInfo,
    playerStep,
    points,
    promote,
    questionTitle,
    realDatetime,
    reset,
    setOppened,
    setShowFeedbacks,
    setUserToChange,
    showFeedbacks,
    showModal,
    status,
    stepTask,
    timeline,
    user,
    userToChange,
    validateTeamMember,
  };
}
